import React, {Component} from 'react'
import RemotingService from "../../../../../services/remoting-service/RemotingService";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import moment from "moment";
import {Button, ButtonGroup} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import PrimeDateRangeInput from "../../../../../components/date-input/PrimeDateRangeInput";
import {TextInput} from "../../../../../components";
import DateUtil from "../../../../../utils/DateUtil";
import confirmDialog from "../../../../../components/dialog/ConfirmDialog";
import NotificationService from "../../../../../services/notification-service/NotificationService";
import AuthService from "../../../../../services/auth-service/AuthService";

export default class UnclaimedInvoiceList extends Component {

    constructor(props) {
        super(props);
        this.state = {
            items: [],
            selectedItems: [],
            dateRange: [new Date(), new Date()],
            billType: 'DIRECT_BILLING',
            patientEid: '',
            patientMrn: '',
            invoiceId: '',
            generating: false
        };
    }

    componentDidMount() {
        this.retrieveItems();
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.selected && this.props.selected) {
            this.retrieveItems();
        }
    }

    handleDateRangeChange = (dateRange) => {
        this.setState({dateRange}, () => {
            // Only fetch when both start and end dates are selected
            if (dateRange && dateRange[0] && dateRange[1]) {
                this.retrieveItems();
            }
        });
    }

    handleBillTypeChange = (billType) => {
        if (billType === this.state.billType) {
            return;
        }
        this.setState({billType, selectedItems: []}, this.retrieveItems);
    }

    retrieveItems = () => {
        const [fromDate, toDate] = this.state.dateRange || [];
        if (!fromDate || !toDate) {
            return;
        }

        const params = {
            fromDate: moment(fromDate).format('YYYY-MM-DD'),
            toDate: moment(toDate).format('YYYY-MM-DD'),
            billType: this.state.billType
        };
        if (this.state.patientEid.trim()) {
            params.patientEid = this.state.patientEid.trim();
        }
        if (this.state.patientMrn.trim()) {
            params.patientMrn = this.state.patientMrn.trim();
        }
        if (this.state.invoiceId.trim()) {
            params.invoiceId = this.state.invoiceId.trim();
        }

        RemotingService.getRemoteCall(
            'api/insurance/coordinator/v2/claims/unclaimed-invoices',
            params,
            (items) => {
                this.setState({items: items, selectedItems: []});
            });
    }

    generateClaims = () => {
        const selectedInvoiceIds = this.state.selectedItems.map(item => item.invoiceId);
        confirmDialog("Generate Claims",
            `Generate claims for ${selectedInvoiceIds.length} selected invoice(s)?`,
            () => {
                this.setState({generating: true});
                RemotingService.postRemoteCall('api/insurance/coordinator/v2/claims/generate-from-invoices',
                    selectedInvoiceIds,
                    (result) => {
                        this.setState({generating: false});
                        if (result.generated.length > 0) {
                            NotificationService.showNotification({
                                severity: 'success',
                                summary: 'Claims Generated',
                                detail: `${result.generated.length} claim(s) generated. Continue in the Claim tab.`
                            });
                        }
                        if (result.skipped.length > 0) {
                            NotificationService.showNotification({
                                severity: 'warn',
                                summary: 'Skipped Invoices',
                                detail: result.skipped.map(s => `Invoice ${s.invoiceId}: ${s.reason}`).join('\n')
                            });
                        }
                        if (result.warnings.length > 0) {
                            NotificationService.showNotification({
                                severity: 'warn',
                                summary: 'Warnings',
                                detail: result.warnings.join('\n')
                            });
                        }
                        this.retrieveItems();
                    }, () => {
                        this.setState({generating: false});
                    });
            });
    }

    render() {
        return (
            <>
                <div className="float-left" style={{display: "flex", gap: "10px"}}>
                    <PrimeDateRangeInput value={this.state.dateRange}
                                         onChange={this.handleDateRangeChange}/>
                    <ButtonGroup style={{height: "fit-content"}}>
                        <Button variant="outline-secondary"
                                active={this.state.billType === 'DIRECT_BILLING'}
                                onClick={() => this.handleBillTypeChange('DIRECT_BILLING')}>
                            Direct Billing
                        </Button>
                        <Button variant="outline-secondary"
                                active={this.state.billType === 'SELF_PAY'}
                                onClick={() => this.handleBillTypeChange('SELF_PAY')}>
                            Self-Pay
                        </Button>
                    </ButtonGroup>
                    <TextInput placeholder="Patient EID" value={this.state.patientEid}
                               onChange={value => this.setState({patientEid: value})}
                               onKeyDown={e => e.key === 'Enter' && this.retrieveItems()}/>
                    <TextInput placeholder="Patient MRN" value={this.state.patientMrn}
                               onChange={value => this.setState({patientMrn: value})}
                               onKeyDown={e => e.key === 'Enter' && this.retrieveItems()}/>
                    <TextInput placeholder="Invoice No" value={this.state.invoiceId}
                               onChange={value => this.setState({invoiceId: value})}
                               onKeyDown={e => e.key === 'Enter' && this.retrieveItems()}/>
                    <Button variant="light" onClick={this.retrieveItems}>
                        <FontAwesomeIcon icon="search"/> Search
                    </Button>
                </div>
                <div className="float-right" style={{display: "flex", gap: "10px"}}>
                    {AuthService.userHasAuthority("UPDATE_INSURANCE_CLAIM") &&
                    <Button variant="success"
                            disabled={this.state.selectedItems.length === 0 || this.state.generating}
                            onClick={this.generateClaims}>
                        {this.state.generating ? 'Generating...' : 'Generate Claims'}
                    </Button>}
                </div>
                <div style={{padding: '10px'}} className="data-table-header-wrapper">
                    <div className="float-left">
                    </div>
                </div>
                <div style={{padding: '10px'}}>
                    <DataTable responsive
                               emptyMessage="No un-claimed invoices found"
                               value={this.state.items}
                               dataKey="invoiceId"
                               selection={this.state.selectedItems}
                               onSelectionChange={e => this.setState({selectedItems: e.value})}>
                        <Column selectionMode="multiple" headerStyle={{width: '3em'}}/>
                        <Column header="Invoice No" field="invoiceId"/>
                        <Column header="Date" field="creationDate"
                                body={item => item.creationDate ? DateUtil.formatDateTime12H(item.creationDate) : ''}/>
                        <Column header="Patient" field="patientName"/>
                        <Column header="Patient EID" field="patientEid"/>
                        <Column header="Patient MRN" field="patientMrn"/>
                        <Column header="Provider" field="providerName"/>
                        <Column header="Clinic" field="clinicName"/>
                        <Column header="Net" field="net"/>
                        <Column header="Approval Code" field="insuranceApprovalCode"/>
                        <Column header="Items" field="itemCount" style={{width: '70px'}}/>
                    </DataTable>
                </div>
            </>
        )
    }
}
